/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import useSWR from "swr"
import { useRouter } from "next/router"
import { useAuthReady } from "./useAuthReady"
import {
  ResponseShopSalePointGetList,
  ResponseShopSalePointGetOne,
  ShopSalePoint,
} from "../libs/api/backend/src"
import {
  UrlShopSalePointFindListAPI,
  UrlShopSalePointFindOneAPI,
} from "../libs/api/url"

export function useShopSalePointId(): string {
  let shopSalePointId = ""

  try {
    const router = useRouter()
    shopSalePointId = (router.query.shopSalePointId as string) || ""
  } catch (err) {
    console.error(err)
  }

  return shopSalePointId
}

export function useShopSalePointFindOne(
  enterpriseId?: string,
  shopSalePointId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopSalePointGetOne>(
    isAuthenticated && enterpriseId && shopSalePointId
      ? UrlShopSalePointFindOneAPI(enterpriseId, shopSalePointId)
      : null
  )

  const shopSalePoint = apiResponse && (apiResponse.data as ShopSalePoint)

  return {
    shopSalePoint: apiResponse && shopSalePoint ? shopSalePoint : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated &&
        enterpriseId &&
        shopSalePointId &&
        !error &&
        !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopSalePointFindAll(
  enterpriseId?: string,
  { shopId }: { shopId?: string } = {}
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopSalePointGetList>(
    isAuthenticated && enterpriseId
      ? UrlShopSalePointFindListAPI(enterpriseId, shopId)
      : null
  )

  const shopSalePoints =
    apiResponse && (apiResponse.data as Array<ShopSalePoint>)

  return {
    shopSalePoints: apiResponse?.data ?? null,
    isLoading: Boolean(
      authLoading || (isAuthenticated && enterpriseId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}
